// ============================================================================
// PsyPatientCabinet — КАБИНЕТ ПАЦИЕНТА психолога (модуль hydra.medic.psy).
//
// Один экран (SSoT данных — либретто lib/psyWorkspace, SSoT прав — сервер):
//   • назначенные методики — psy_assignments пациента;
//   • прохождение — по ССЫЛКЕ назначения или короткому КОДУ: открывается
//     существующая публичная страница /poll/... (PollGuestPage + FillRunner),
//     второго раннера/протокола здесь НЕТ;
//   • результаты и история — протоколы типов опросов (subject_id = пациент) и
//     все назначения (действующие/выполненные/отменённые);
//   • явные состояния: нет назначений, нет результата, нет прав, нет связи
//     карточки пациента с учётной записью, ошибка (+ «Повторить»).
//
// Режимы:
//   • patientId передан — кабинет пациента в рабочем месте психолога
//     (canManage=true: выдача ссылки/кода и ручной результат);
//   • patientId НЕ передан — СВОЙ кабинет: карточка пациента определяется по
//     контактам учётной записи (телефон/почта; неоднозначность — явный отказ).
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarDays, ClipboardList, ExternalLink, FlaskConical, Loader2, RefreshCw, Share2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/CopyButton';
import { useAuthStore } from '@/store/authStore';
import { friendlyError, isPermissionDeniedError, logErrorToJournal } from '@/lib/errors';
import {
  assignmentStatusLabel, buildCabinetRows, loadPsyCabinet, pollCodeEntry, pollLink,
  resolveMyPatient, shortCode, type ActiveLink, type CabinetRow, type PsyCabinetData,
  type PsyMethod,
} from '@/lib/psyWorkspace';
import { PsyAssignDialog } from '@/components/psy/PsyAssignDialog';
import { PsyManualResultDialog } from '@/components/psy/PsyManualResultDialog';
import { PsyResultsTable } from '@/components/psy/PsyResultsTable';

export interface PsyPatientCabinetProps {
  /** Карточка пациента; не передан — свой кабинет (по контактам учётной записи). */
  patientId?: string;
  patientName?: string;
  /** Психолог: выдача ссылки/кода и внесение ручного результата. */
  canManage?: boolean;
}

type Resolution =
  | { state: 'fixed'; id: string; name: string }
  | { state: 'resolving' }
  | { state: 'none' }
  | { state: 'ambiguous'; count: number }
  | { state: 'error'; message: string };

function origin(): string {
  return typeof window !== 'undefined' ? window.location.origin : '';
}

/** Открыть существующую страницу прохождения (ссылка/код) — новый экран. */
function openExternal(url: string): void {
  const w = window.open(url, '_blank', 'noopener,noreferrer');
  if (!w) window.location.assign(url);
}

export function PsyPatientCabinet({ patientId, patientName, canManage = false }: PsyPatientCabinetProps) {
  const user = useAuthStore((s) => s.user);
  const [resolution, setResolution] = useState<Resolution>(
    patientId
      ? { state: 'fixed', id: patientId, name: patientName || 'Пациент' }
      : { state: 'resolving' },
  );
  const [resolveTick, setResolveTick] = useState(0);
  const [data, setData] = useState<PsyCabinetData | null>(null);
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [assignMethod, setAssignMethod] = useState<PsyMethod | null>(null);
  const [manualMethod, setManualMethod] = useState<PsyMethod | null>(null);

  // Связь «учётная запись ↔ карточка пациента» — только для своего кабинета.
  useEffect(() => {
    if (patientId) {
      setResolution({ state: 'fixed', id: patientId, name: patientName || 'Пациент' });
      return;
    }
    let cancelled = false;
    (async () => {
      setResolution({ state: 'resolving' });
      try {
        const r = await resolveMyPatient(String(user?.phone || ''), String(user?.email || ''));
        if (cancelled) return;
        if (r.status === 'found') {
          setResolution({ state: 'fixed', id: r.patient.patientId, name: r.patient.patientName });
        } else if (r.status === 'ambiguous') {
          setResolution({ state: 'ambiguous', count: r.count });
        } else {
          setResolution({ state: 'none' });
        }
      } catch (e) {
        if (cancelled) return;
        logErrorToJournal(e, 'Кабинет пациента: определение карточки пациента');
        setResolution({ state: 'error', message: friendlyError(e) });
      }
    })();
    return () => { cancelled = true; };
  }, [patientId, patientName, user?.phone, user?.email, resolveTick]);

  const resolvedId = resolution.state === 'fixed' ? resolution.id : '';

  const load = useCallback(async () => {
    if (!resolvedId) return;
    setLoading(true);
    try {
      const r = await loadPsyCabinet(resolvedId);
      setData(r);
      setAccessDenied(false);
      setLoadError('');
    } catch (e) {
      setData(null);
      if (isPermissionDeniedError(e)) {
        // Права — SSoT сервера: честное ограничение, а не пустой список.
        setAccessDenied(true);
        setLoadError('');
      } else {
        logErrorToJournal(e, `Кабинет пациента: загрузка (${resolvedId})`);
        setAccessDenied(false);
        setLoadError(friendlyError(e));
      }
    } finally {
      setLoading(false);
    }
  }, [resolvedId]);

  useEffect(() => { void load(); }, [load]);

  const rows = useMemo(
    () => (data ? buildCabinetRows(data) : { active: [], history: [] }),
    [data],
  );

  const reload = useCallback(() => {
    if (resolution.state === 'error' || resolution.state === 'none' || resolution.state === 'ambiguous') {
      setResolveTick((n) => n + 1);
      return;
    }
    void load();
  }, [load, resolution.state]);

  const title = resolution.state === 'fixed' && canManage
    ? `Кабинет пациента: ${resolution.name}`
    : 'Кабинет пациента';

  return (
    <div className="h-full min-h-0 overflow-auto p-3 space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <FlaskConical className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">{title}</span>
        {resolution.state === 'fixed' && !canManage && (
          <span className="text-[11px] text-muted-foreground truncate max-w-[40%]" title={resolution.name}>
            {resolution.name}
          </span>
        )}
        {data && (
          <span className="text-[11px] text-muted-foreground">
            назначено: {rows.active.length} · результатов: {data.results.length}
          </span>
        )}
        <Button size="sm" variant="outline" className="h-7 px-2 ml-auto"
          onClick={reload} disabled={loading} title="Обновить">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </div>

      {resolution.state === 'resolving' && (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />Определяем карточку пациента…
        </div>
      )}
      {resolution.state === 'none' && (
        <StateBox
          title="Карточка пациента не найдена"
          text="Телефон и почта учётной записи не совпадают с карточкой в реестре пациентов. Обратитесь к специалисту: он свяжет учётную запись с карточкой или выдаст ссылку/код на тест."
          onRetry={reload}
        />
      )}
      {resolution.state === 'ambiguous' && (
        <StateBox
          title={`Найдено несколько карточек (${resolution.count})`}
          text="Автоматически выбрать одну нельзя — это медицинские данные. Обратитесь к специалисту, чтобы он указал вашу карточку."
          onRetry={reload}
        />
      )}
      {resolution.state === 'error' && (
        <StateBox title="Не удалось определить карточку пациента" text={resolution.message} onRetry={reload} />
      )}

      {accessDenied && (
        <StateBox
          title="Нет доступа к назначениям методик"
          text={canManage
            ? 'Роли не выдано право «Чтение» на справочник назначений (psy_assignments). Права назначает администратор базы.'
            : 'Роли «Пациент» не выдано право «Чтение» на назначения (psy_assignments). Обратитесь к специалисту или администратору базы.'}
          onRetry={reload}
        />
      )}
      {loadError && <StateBox title="Не удалось загрузить кабинет" text={loadError} onRetry={reload} />}

      {!accessDenied && !loadError && resolution.state === 'fixed' && !data && loading && (
        <div className="text-xs text-muted-foreground">Загрузка кабинета…</div>
      )}

      {!accessDenied && !loadError && data && (
        <>
          <section>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
              Назначенные тесты
            </div>
            {rows.active.length === 0 ? (
              <div className="text-xs text-muted-foreground border rounded-lg px-3 py-2">
                {canManage
                  ? 'Активных назначений нет. Назначьте методику на вкладке «Тесты и результаты».'
                  : 'Назначений нет. Специалист назначит методику — она появится здесь со ссылкой и кодом.'}
                {rows.history.length > 0 && ' Выполненные и отменённые назначения — ниже.'}
              </div>
            ) : (
              <div className="grid gap-2 lg:grid-cols-2">
                {rows.active.map((row) => (
                  <AssignmentCard
                    key={row.assignment.id}
                    row={row}
                    canManage={canManage}
                    onOpenLink={(link) => openExternal(pollLink(origin(), link.token))}
                    onEnterCode={() => openExternal(pollCodeEntry(origin()))}
                    onManage={(m) => setAssignMethod(m)}
                  />
                ))}
              </div>
            )}
          </section>

          <section>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1">
              <ClipboardList className="h-3 w-3" />Результаты
            </div>
            {data.resultsDenied ? (
              <div className="text-xs text-muted-foreground border rounded-lg px-3 py-2">
                {canManage
                  ? 'Право чтения протоколов роли не выдано — назначьте право «Чтение» на типы опросов.'
                  : 'Просмотр результатов не разрешён роли «Пациент». Результаты ведёт и интерпретирует специалист.'}
              </div>
            ) : (
              <PsyResultsTable
                results={data.results}
                emptyText="Результатов пока нет — пройдите назначенный тест по ссылке или коду."
              />
            )}
          </section>

          <section>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1">
              <CalendarDays className="h-3 w-3" />История назначений
            </div>
            {rows.history.length === 0 ? (
              <div className="text-xs text-muted-foreground">История пуста.</div>
            ) : (
              <div className="space-y-1.5">
                {rows.history.map((row) => (
                  <div key={row.assignment.id} className="flex items-start gap-2 rounded-lg border bg-card px-3 py-2 text-xs">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">
                        {row.method?.synonym || row.assignment.methodCode || 'Методика'}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {row.assignment.assignedDate || '—'} · {assignmentStatusLabel(row.assignment.status)}
                      </div>
                    </div>
                    <span className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] ${
                      row.lastResult
                        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                        : 'border-border bg-muted/50 text-muted-foreground'
                    }`}>
                      {row.lastResult ? 'есть результат' : 'результата нет'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {assignMethod && resolution.state === 'fixed' && (
        <PsyAssignDialog
          patientId={resolution.id}
          patientName={resolution.name}
          method={assignMethod}
          assignment={rows.history.find((r) => r.method?.code === assignMethod.code)?.assignment || null}
          onClose={() => { setAssignMethod(null); void load(); }}
          onManual={() => { setManualMethod(assignMethod); setAssignMethod(null); }}
        />
      )}
      {manualMethod && resolution.state === 'fixed' && (
        <PsyManualResultDialog
          patientId={resolution.id}
          method={manualMethod}
          onClose={() => { setManualMethod(null); void load(); }}
        />
      )}
    </div>
  );
}

/** Карточка действующего назначения: методика, ссылка и короткий код. */
export function AssignmentCard({ row, canManage, onOpenLink, onEnterCode, onManage }: {
  row: CabinetRow;
  canManage: boolean;
  onOpenLink: (link: ActiveLink) => void;
  onEnterCode: () => void;
  onManage: (method: PsyMethod) => void;
}) {
  const method = row.method;
  const code = row.link ? shortCode(row.link.token).toUpperCase() : '';
  return (
    <div className="border rounded-lg p-2.5 bg-card flex flex-col gap-1.5">
      <div className="flex items-start gap-2">
        <div className="text-sm font-medium min-w-0">{method?.synonym || row.assignment.methodCode || 'Методика'}</div>
        <div className="ml-auto flex gap-1 shrink-0">
          {row.lastResult ? (
            <span className="text-[10px] rounded-full bg-green-500/10 text-green-700 dark:text-green-400 px-1.5 py-0.5">
              есть результат
            </span>
          ) : (
            <span className="text-[10px] rounded-full bg-primary/10 text-primary px-1.5 py-0.5">
              ожидает прохождения
            </span>
          )}
        </div>
      </div>
      {method?.description && (
        <div className="text-[11px] text-muted-foreground line-clamp-2">{method.description}</div>
      )}
      {!method && row.assignment.methodCode && (
        <div className="text-[11px] text-destructive">
          Методика «{row.assignment.methodCode}» отсутствует в банке методик базы.
        </div>
      )}
      <div className="text-[10px] text-muted-foreground">
        назначено: {row.assignment.assignedDate || '—'}
        {row.link?.expiresAt
          ? ` · ссылка действует до ${new Date(row.link.expiresAt).toLocaleString('ru-RU')}`
          : ''}
      </div>

      {row.link ? (
        <div className="space-y-1.5 pt-1">
          <div className="flex items-center gap-1 flex-wrap">
            <Button size="sm" className="h-7 px-2 text-[11px]" onClick={() => onOpenLink(row.link as ActiveLink)}>
              <ExternalLink className="h-3 w-3 mr-1" />Пройти по ссылке
            </Button>
            <span className="font-mono text-xs tracking-wider border rounded px-1.5 py-0.5 bg-muted/30">{code}</span>
            <CopyButton text={code} compact />
          </div>
          <div className="text-[10px] text-muted-foreground">
            {canManage
              ? 'Передайте пациенту ссылку или код из 8 символов — он откроет страницу прохождения.'
              : 'Или откройте страницу прохождения и введите код из 8 символов.'}
          </div>
        </div>
      ) : (
        <div className="space-y-1.5 pt-1">
          <div className="text-[11px] text-muted-foreground">
            {canManage
              ? 'Ссылка и код ещё не выданы — выдайте их пациенту.'
              : 'Ссылка и код ещё не выданы — их передаёт специалист.'}
          </div>
          {!canManage && (
            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={onEnterCode}>
              Ввести код
            </Button>
          )}
        </div>
      )}

      {canManage && method && (
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => onManage(method)}>
            <Share2 className="h-3 w-3 mr-1" />Ссылка и код
          </Button>
        </div>
      )}
    </div>
  );
}

/** Явное состояние экрана: заголовок, текст, «Повторить» (если есть запрос). */
function StateBox({ title, text, onRetry }: { title: string; text: string; onRetry?: () => void }) {
  return (
    <div className="text-xs border rounded-lg px-3 py-2 bg-card space-y-1.5">
      <div className="text-sm font-medium">{title}</div>
      {text && <div className="text-muted-foreground break-words">{text}</div>}
      {onRetry && (
        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={onRetry}>
          Повторить
        </Button>
      )}
    </div>
  );
}
